import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, History as HistoryIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatNotes, hasVisibleNotes } from "@/lib/safeDisplay";
import { ReasonModal } from "@/components/ReasonModal";
import { CANCEL_REQUEST_REASONS } from "@/lib/lists";
import { getLifecycleStatus, lifecycleBadgeVariant } from "@/lib/withdrawGate";

const INQUIRY_STATUS_FALLBACK = [
  "New Inquiry", "Awaiting Information", "Rights Review", "Legal Review Required",
  "Quote Preparation", "Negotiation", "Declined", "Closed", "Licensed",
];

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function Read({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`text-[13px] ${mono ? "font-mono" : ""}`} data-testid={`sync-opportunity-read-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        {value || <span className="text-muted-foreground italic">\u2014</span>}
      </div>
    </div>
  );
}

export default function SyncOpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writer = canWrite(user);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statuses, setStatuses] = useState(INQUIRY_STATUS_FALLBACK);
  const [history, setHistory] = useState([]);

  const [form, setForm] = useState({
    status: "",
    composition_readiness: "",
    master_readiness: "",
    contract_status: "",
    rights_status: "",
    executive_decision: "",
    outcome: "",
    internal_notes: "",
  });

  function setField(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  // Phase A Run 2 follow-up — cancel modal wire-in.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const lifecycleStatus = getLifecycleStatus(doc);
  const isCancelled = lifecycleStatus === "CANCELLED";
  async function submitCancel({ category, detail }) {
    setCancelBusy(true);
    try {
      await api.post(`/gfmp/inquiries/${id}/cancel`, { category, detail });
      toast.success("Sync opportunity cancelled. Audit record created.");
      setCancelOpen(false);
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelBusy(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/gfmp/inquiries/${id}`);
      setDoc(r.data);
      setForm({
        status: r.data.status || "",
        composition_readiness: r.data.composition_readiness || "",
        master_readiness: r.data.master_readiness || "",
        contract_status: r.data.contract_status || "",
        rights_status: r.data.rights_status || "",
        executive_decision: r.data.executive_decision || "",
        outcome: r.data.outcome || "",
        internal_notes: r.data.internal_notes || "",
      });
      const audit = await api.get("/audit", { params: { record_type: "gfmp_inquiry", record_id: id, limit: 50 } });
      setHistory(Array.isArray(audit.data) ? audit.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load opportunity."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!writer) return;
    load();
    api.get("/gfmp/meta").then((r) => {
      if (Array.isArray(r.data?.inquiry_statuses)) setStatuses(r.data.inquiry_statuses);
    }).catch(() => {});
  }, [writer, load]);

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/gfmp/inquiries/${id}`, form);
      setDoc(r.data);
      toast.success("Sync opportunity updated.");
      const audit = await api.get("/audit", { params: { record_type: "gfmp_inquiry", record_id: id, limit: 50 } });
      setHistory(Array.isArray(audit.data) ? audit.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not save changes."));
    } finally {
      setSaving(false);
    }
  }

  if (!writer) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="sync-opportunity-readonly">
        <Alert>
          <AlertDescription>
            Sync Opportunity records are restricted to Catalog Manager, GFMP Reviewer, and Super Admin.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading || !doc) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-8" data-testid="sync-opportunity-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading\u2026
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sync-opportunity-detail-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/sync-opportunities")} data-testid="sync-opportunity-back">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> All opportunities
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            Inquiry · <span className="font-mono normal-case">{doc.id?.slice(0, 8)}</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl tracking-tight">
            {doc.project_title || "(no project title)"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            From <span className="font-medium text-foreground">{doc.contact_name}</span>
            {doc.company ? `, ${doc.company}` : ""} · {doc.email}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide" data-testid="sync-opportunity-current-status">
            {doc.status}
          </Badge>
          {isCancelled && (
            <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${lifecycleBadgeVariant(lifecycleStatus).className}`} data-testid="sync-opportunity-lifecycle-badge">
              {lifecycleBadgeVariant(lifecycleStatus).label}
            </Badge>
          )}
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Received {fmtDate(doc.created_at)}
          </span>
          {!isCancelled && (
            <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} data-testid="sync-opportunity-cancel-button" style={{borderColor:"hsl(0 70% 42%)", color:"hsl(0 70% 42%)"}}>
              Cancel Opportunity
            </Button>
          )}
        </div>
      </div>

      <Alert data-testid="sync-opportunity-banner">
        <AlertDescription className="text-[12.5px]">
          No licence, quote, or rights representation may be issued until rights authority and
          contract status are confirmed.
        </AlertDescription>
      </Alert>

      {/* Inquiry payload (read-only) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inquiry details</CardTitle>
          <CardDescription>Verbatim payload submitted through the public Sync Catalogue.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Read label="Contact name" value={doc.contact_name} />
          <Read label="Company / organisation" value={doc.company} />
          <Read label="Email" value={doc.email} />
          <Read label="Phone" value={doc.phone} />
          <Read label="Project title" value={doc.project_title} />
          <Read label="Project type" value={doc.project_type} />
          <Read label="Territory" value={doc.territory} />
          <Read label="Requested term" value={doc.requested_term} />
          <Read label="Budget range" value={doc.budget_range} />
          <Read label="Deadline" value={doc.deadline} />
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Intended usage</div>
            <div className="text-[13px] whitespace-pre-wrap" data-testid="sync-opportunity-read-usage">
              {doc.intended_usage || <span className="text-muted-foreground italic">\u2014</span>}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Tracks of interest</div>
            {doc.tracks_of_interest_titles?.length > 0 ? (
              <ul className="flex flex-wrap gap-1.5 mt-1" data-testid="sync-opportunity-tracks">
                {doc.tracks_of_interest_titles.map((t, i) => (
                  <li key={i} className="px-2 py-1 rounded-full text-[11px]" style={{ background: "hsl(347 60% 22% / 0.08)", color: "hsl(347 60% 22%)" }}>
                    {t}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-[12.5px] text-muted-foreground italic">No specific tracks referenced.</div>
            )}
          </div>
          <div className="sm:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Additional notes</div>
            <div className="text-[13px] whitespace-pre-wrap" data-testid="sync-opportunity-read-additional">
              {doc.additional_notes || <span className="text-muted-foreground italic">\u2014</span>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Internal triage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Internal triage</CardTitle>
          <CardDescription>
            Update status, rights/contract review notes, executive decision, and outcome. These
            fields are internal only and never appear on the public catalogue.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setField("status", v)}>
                <SelectTrigger data-testid="sync-opportunity-status-select">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="executive_decision">Executive decision</Label>
              <Input id="executive_decision" value={form.executive_decision} onChange={(e) => setField("executive_decision", e.target.value)}
                     maxLength={400} placeholder="e.g. proceed, decline, escalate\u2026"
                     data-testid="sync-opportunity-executive-decision" />
            </div>
            <div>
              <Label htmlFor="composition_readiness">Composition readiness</Label>
              <Input id="composition_readiness" value={form.composition_readiness} onChange={(e) => setField("composition_readiness", e.target.value)}
                     maxLength={400} data-testid="sync-opportunity-composition" />
            </div>
            <div>
              <Label htmlFor="master_readiness">Master readiness</Label>
              <Input id="master_readiness" value={form.master_readiness} onChange={(e) => setField("master_readiness", e.target.value)}
                     maxLength={400} data-testid="sync-opportunity-master" />
            </div>
            <div>
              <Label htmlFor="contract_status">Contract status</Label>
              <Input id="contract_status" value={form.contract_status} onChange={(e) => setField("contract_status", e.target.value)}
                     maxLength={400} data-testid="sync-opportunity-contract" />
            </div>
            <div>
              <Label htmlFor="rights_status">Rights status</Label>
              <Input id="rights_status" value={form.rights_status} onChange={(e) => setField("rights_status", e.target.value)}
                     maxLength={400} data-testid="sync-opportunity-rights" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="outcome">Outcome</Label>
              <Input id="outcome" value={form.outcome} onChange={(e) => setField("outcome", e.target.value)}
                     maxLength={400} data-testid="sync-opportunity-outcome" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="internal_notes">Internal notes</Label>
              <Textarea id="internal_notes" rows={4} value={form.internal_notes} onChange={(e) => setField("internal_notes", e.target.value)}
                        maxLength={4000} data-testid="sync-opportunity-internal-notes" />
            </div>
          </div>

          <Separator />

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={load} disabled={saving} data-testid="sync-opportunity-revert">
              Revert
            </Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="sync-opportunity-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit history */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <HistoryIcon className="w-4 h-4" /> Audit history
          </CardTitle>
          <CardDescription>All inquiry lifecycle changes recorded against this record.</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-[12.5px] text-muted-foreground italic">No audit entries yet.</div>
          ) : (
            <ol className="space-y-2" data-testid="sync-opportunity-history-list">
              {history.map((h) => (
                <li key={h.audit_id} className="rounded-md border p-2.5 text-[12px]">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{h.action_type}</span>
                    <span className="text-muted-foreground font-mono">{fmtDate(h.timestamp)}</span>
                  </div>
                  <div className="text-muted-foreground mt-1">
                    by <span className="text-foreground">{h.user_name || h.user_email || "\u2014"}</span>
                    {hasVisibleNotes(h.notes) && <span> · {formatNotes(h.notes, { fallback: "" })}</span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      <ReasonModal
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel Sync Opportunity"
        description="Soft-cancels this opportunity. Prior notes, files, and audit history are preserved. Reason is required."
        categoryOptions={CANCEL_REQUEST_REASONS}
        confirmLabel="Cancel Opportunity"
        severity="high"
        busy={cancelBusy}
        testid="cancel-sync-opportunity-modal"
        onConfirm={submitCancel}
      />
    </div>
  );
}
